import {
    createContext,
    createElement,
    type ReactNode,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { qsParse, qsStringify } from './query-string';
import { filterStringsToEnumValues } from './typescript';

export type Strategy = 'push' | 'replace';
type QueryParamUpdates = {
    [key: string]: string | string[] | null | undefined;
};

type EnqueueQueryParamUpdate = (
    key: string,
    value: QueryParamUpdates[string],
    strategy: Strategy,
) => void;

type PendingQueryParamBatch = {
    pathname: string;
    params: QueryParamUpdates;
    strategy: Strategy;
};

type QueryParamLocation = { pathname: string; search: string; state?: unknown };
type QueryParamRouterSnapshot = {
    location: QueryParamLocation;
    pendingLocations: QueryParamLocation[];
};

const QueryParamUpdateContext = createContext<EnqueueQueryParamUpdate | null>(null);

export const QueryParamProvider = ({
    children,
    getRouterSnapshot,
}: {
    children?: ReactNode;
    getRouterSnapshot: () => QueryParamRouterSnapshot;
}) => {
    const navigate = useNavigate();
    const getRouterSnapshotRef = useRef(getRouterSnapshot);
    getRouterSnapshotRef.current = getRouterSnapshot;

    const pendingRef = useRef<PendingQueryParamBatch | null>(null);

    useEffect(
        () => () => {
            pendingRef.current = null;
        },
        [],
    );

    const enqueueUpdate: EnqueueQueryParamUpdate = (key, value, strategy) => {
        const { location: currentLocation } = getRouterSnapshotRef.current();
        let pending = pendingRef.current;

        if (pending && pending.pathname !== currentLocation.pathname) {
            pending = null;
            pendingRef.current = null;
        }

        if (!pending) {
            const batch: PendingQueryParamBatch = {
                pathname: currentLocation.pathname,
                params: {},
                strategy: 'replace',
            };
            pending = batch;
            pendingRef.current = batch;

            Promise.resolve().then(() => {
                if (pendingRef.current !== batch) {
                    return;
                }

                pendingRef.current = null;
                const { location, pendingLocations } = getRouterSnapshotRef.current();
                if (
                    batch.pathname !== location.pathname ||
                    pendingLocations.some(({ pathname }) => pathname !== batch.pathname)
                ) {
                    return;
                }

                const latestLocation =
                    pendingLocations.find(({ pathname }) => pathname === batch.pathname) ??
                    location;

                const nextQuery = qsStringify({
                    ...qsParse(latestLocation.search),
                    ...batch.params,
                });
                const search = nextQuery ? `?${nextQuery}` : '';

                navigate(
                    { pathname: latestLocation.pathname, search },
                    { replace: batch.strategy === 'replace', state: latestLocation.state },
                );
            });
        }

        pending.params[key] = value;
        if (strategy === 'push') {
            pending.strategy = strategy;
        }
    };

    return createElement(QueryParamUpdateContext.Provider, { value: enqueueUpdate }, children);
};

const useEnqueueQueryParamUpdate = () => {
    const enqueueUpdate = useContext(QueryParamUpdateContext);
    if (!enqueueUpdate) {
        throw new Error('useQueryParam must be used within QueryParamProvider');
    }
    return enqueueUpdate;
};

export function usePersistURLInSessionStorage() {
    const navigate = useNavigate();
    const { pathname: key, search } = useLocation();
    const [isInitialized, setIsInitialized] = useState(false);
    const hasRestoredFromSessionRef = useRef<string | null>(null);

    // Update session storage when the search changes
    useEffect(() => {
        const keyValue = key.length && key[key.length - 1] === '/' ? key.slice(0, -1) : key;
        if (!isInitialized) {
            setIsInitialized(true);
            return;
        }
        sessionStorage.setItem(keyValue, search);
    }, [search, key, isInitialized]);

    // Update the URL from session storage when the page loads - per pathname
    useEffect(() => {
        const keyValue = key.length && key[key.length - 1] === '/' ? key.slice(0, -1) : key;

        // Only restore once per pathname to avoid interfering with tab navigation
        if (hasRestoredFromSessionRef.current === keyValue) {
            return;
        }

        hasRestoredFromSessionRef.current = keyValue;

        // Only restore if there's no current search params (fresh navigation)
        if (!search) {
            const value = sessionStorage.getItem(keyValue);
            if (value) {
                navigate(
                    {
                        search: value,
                    },
                    { replace: true },
                );
            }
        }
    }, [key, search, navigate]);
}

// Overload for string parameters (backward compatibility)
export function useQueryParam(
    key: string,
    defaultValue?: string | null,
): [string | null, (newValue: string | null, strategy?: Strategy) => void];

// Overload for typed parameters (use-query-params compatibility)
export function useQueryParam<T>(
    key: string,
    paramConfig: QueryParamConfig<T>,
): [T, (newValue: T, strategy?: Strategy) => void];

// Implementation
export function useQueryParam<T>(
    key: string,
    paramConfigOrDefault?: QueryParamConfig<T> | string | null,
): [T | string | null, (newValue: T | string | null, strategy?: Strategy) => void] {
    const location = useLocation();
    const enqueueUpdate = useEnqueueQueryParamUpdate();
    const queryParams = qsParse(location.search);

    // Determine if we're using typed params or simple string params
    const isTypedParam =
        paramConfigOrDefault != null &&
        typeof paramConfigOrDefault === 'object' &&
        'encode' in paramConfigOrDefault;
    const paramConfig = isTypedParam ? (paramConfigOrDefault as QueryParamConfig<T>) : null;
    const defaultValue = !isTypedParam ? (paramConfigOrDefault as string | null) : null;

    const setValue = (newValue: T | string | null, strategy: Strategy = 'replace') => {
        let encodedValue: string | string[] | null | undefined;
        if (paramConfig) {
            const encoded = paramConfig.encode(newValue as T);
            // Convert encoded value to proper type
            if (Array.isArray(encoded)) {
                encodedValue = encoded.filter((v): v is string => v != null);
            } else {
                encodedValue = encoded || undefined;
            }
        } else {
            encodedValue = (newValue as string | null) === null ? undefined : (newValue as string);
        }

        enqueueUpdate(key, encodedValue, strategy);
    };

    const rawValue = queryParams[key];

    if (paramConfig) {
        // Use typed parameter config
        const value = paramConfig.decode(rawValue);
        return [value, setValue];
    } else {
        // Use simple string logic
        if (rawValue === undefined) {
            return [defaultValue || null, setValue];
        }
        const value = Array.isArray(rawValue) ? rawValue[0] : rawValue || null;
        return [value, setValue];
    }
}

export function useQueryArrayParam(
    key: string,
    defaultValue: string[] = [],
): [string[], (newValue: string[], strategy?: Strategy) => void] {
    const location = useLocation();
    const enqueueUpdate = useEnqueueQueryParamUpdate();
    const queryParams = qsParse(location.search);

    const setValue = (newValue: string[], strategy: Strategy = 'push') => {
        enqueueUpdate(key, newValue, strategy);
    };

    if (queryParams[key] === undefined) {
        return [defaultValue, setValue];
    }

    const rawValue = queryParams[key];
    let value: string[];
    if (Array.isArray(rawValue)) {
        value = rawValue.filter((v): v is string => v !== null);
    } else if (rawValue) {
        value = [rawValue];
    } else {
        value = [];
    }
    return [value, setValue];
}

export function useQueryEnumArrayParam<TEnum extends string>(
    key: string,
    enumType: Record<any, TEnum>,
    defaultValue: string[] = [],
): [TEnum[], (newValue: TEnum[], strategy?: Strategy) => void] {
    const [rawValue, setValue] = useQueryArrayParam(key, defaultValue);
    return [filterStringsToEnumValues(rawValue, enumType), setValue];
}

// Parameter type definitions (compatible with use-query-params)
export interface QueryParamConfig<T> {
    encode: (value: T) => string | (string | null)[] | null | undefined;
    decode: (value: string | (string | null)[] | null | undefined) => T;
}

// String parameter handler
export const StringParam: QueryParamConfig<string | null | undefined> = {
    encode: (value) => value || undefined,
    decode: (value) => {
        if (Array.isArray(value)) {
            return value[0] || undefined;
        }
        return value || undefined;
    },
};

// Array parameter handler
export const ArrayParam: QueryParamConfig<string[]> = {
    encode: (value) => (value.length ? value : undefined),
    decode: (value) => {
        if (Array.isArray(value)) {
            return value.filter((v): v is string => v != null);
        }
        if (value) {
            return [value];
        }
        return [];
    },
};

// Boolean parameter handler
export const BooleanParam: QueryParamConfig<boolean | null> = {
    encode: (value) => {
        if (value === true) return '1';
        if (value === false) return '0';
        return undefined;
    },
    decode: (value) => {
        if (Array.isArray(value)) {
            value = value[0];
        }
        if (value === '1' || value === 'true') return true;
        if (value === '0' || value === 'false') return false;
        return null;
    },
};

// JSON parameter handler
export const JsonParam: QueryParamConfig<any> = {
    encode: (value) => {
        try {
            return value != null ? JSON.stringify(value) : undefined;
        } catch {
            return undefined;
        }
    },
    decode: (value) => {
        if (Array.isArray(value)) {
            value = value[0];
        }
        if (!value) return undefined;
        try {
            return JSON.parse(value);
        } catch {
            return undefined;
        }
    },
};

// withDefault utility
export function withDefault<T>(param: QueryParamConfig<T>, defaultValue: T): QueryParamConfig<T> {
    return {
        encode: param.encode,
        decode: (value) => {
            const decoded = param.decode(value);
            return decoded != null ? decoded : defaultValue;
        },
    };
}

// Create enum parameter handlers for backward compatibility
export function createEnumParam<T extends string>(enumValues: T[]) {
    return {
        encode: (value: T | null) => value,
        decode: (value: string | (string | null)[] | null | undefined): T | null => {
            if (Array.isArray(value)) {
                value = value[0];
            }
            if (value && enumValues.includes(value as T)) {
                return value as T;
            }
            return null;
        },
    };
}

export function createEnumArrayParam<T extends string>(enumValues: T[]) {
    return {
        encode: (value: T[]) => (value.length ? value : undefined),
        decode: (value: string | (string | null)[] | null | undefined): T[] => {
            if (Array.isArray(value)) {
                return value.filter((v): v is T => v != null && enumValues.includes(v as T));
            }
            if (value && enumValues.includes(value as T)) {
                return [value as T];
            }
            return [];
        },
    };
}
